/**
 * User-navigation notification tap handlers.
 *
 * Web port of the user-facing destinations of Flutter's
 * `PushNavigationService.onPushNotificationAppUpdates` switch
 * (`lib/services/push_navigation_service.dart:78-140`, `:184-193`, `:203-209`,
 * `:229-233`) — thirteen cases that all land on a *user*-scoped destination:
 * user details, the subscribers list, the premium-packages screen, campaigns, or
 * the creator-side review sheet.
 *
 * Exports and the `AppNotificationType` cases each one serves (this list must
 * match the Task 13 router's case table exactly):
 *  - `handleSubscribersNotificationTap` (1): `exclusiveSubscription` (`:78-82`).
 *  - `handleUserDetailsNotificationTap` (6): `exclusiveSubscriptionExpired`
 *    (`:83-89`), `exclusiveSubscriptionRenewd` (`:90-96`),
 *    `exclusiveSubscriptionRequestCancelled` (`:113-119`),
 *    `exclusiveSubscriptionRequestAccepted` (`:120-126`),
 *    `exclusiveSubscriptionRequestDeclined` (`:127-133`),
 *    `exclusiveSubscriptionFailedInsufficientCoins` (`:134-140`).
 *  - `handleExclusiveSubscriptionRequestedTap` (1):
 *    `exclusiveSubscriptionRequested` (`:97-112`).
 *  - `handleReportUserNotificationTap` (1): `reportUser` (`:184-193`).
 *  - `handlePremiumPackagesNotificationTap` (3): `premiumPackage`,
 *    `identityVerification`, `premiumPackageExpired` — one shared Flutter case
 *    body (`:203-209`).
 *  - `handleCampaignsNotificationTap` (1): `campaignRanking` (`:229-233`).
 * 1 + 6 + 1 + 1 + 3 + 1 = 13.
 *
 * Deliberate web adaptation — awaited-vs-not. Flutter `await`s the navigation on
 * `exclusiveSubscription` / `exclusiveSubscriptionExpired` /
 * `exclusiveSubscriptionRenewd` (`:80`, `:85`, `:92`) and does not on the other
 * ten. A Dart route future completes when the pushed route is *popped*, so those
 * three defer the caller's follow-up (mark-read) until the user comes back. Web
 * `Navigator.*` returns `void` and has no pop-future, so every case here resolves
 * immediately and the caller's mark-read (Task 14) fires right after dispatch.
 * The end state is identical; this is structural, not a behaviour change.
 *
 * The three flagless handlers take no arguments, matching the Task 13 router's
 * call table (`handleSubscribersNotificationTap()` etc.) — they have nothing to
 * read off the context.
 *
 * Pending caller: `notificationTap/notificationTapRouter.ts` (Task 13) is the
 * first and only consumer — this module is intentionally uncalled for exactly
 * one task, not dead code.
 */
import { Navigator } from '@/presentation/routing/Navigator';
import { ReviewExclusiveSubscriptionSheet } from '@/presentation/feature/userDetails/components/reviewExclusiveSubscriptionSheet/ReviewExclusiveSubscriptionSheet';
import type { NotificationTapContext } from '@/presentation/feature/inbox/notificationTap/notificationTapContext';

/**
 * Navigates to the notification's subject user
 * (`push_navigation_service.dart:83-140` — every plain `navigateToUserDetailsScreen`
 * case in the exclusive-subscription block, all of which read
 * `notification.info.usersInfo.userId`).
 */
export const handleUserDetailsNotificationTap = (context: NotificationTapContext): void => {
  Navigator.navigateToUserDetails(context.notification.info.usersInfo.userId);
};

/**
 * `exclusiveSubscriptionRequested` (`push_navigation_service.dart:97-112`).
 *
 * Flutter's gate, ported verbatim: an already-reviewed request
 * (`notification.info.actioned != null && notification.info.actioned!`, `:99-100`)
 * just opens the requester's profile; an un-reviewed one opens the creator-side
 * review sheet (`:106-110`). The web entity types `actioned?: boolean`, so
 * `=== true` is the exact equivalent of that null-check-plus-assert.
 *
 * This handler is the caller `ReviewExclusiveSubscriptionSheet` was built for
 * (Task 6) — the gate belongs here, in the tap dispatcher, exactly as in Flutter.
 * The sheet — not this handler — owns the `reviewExclusiveSubscription` callable
 * and its result; nothing on this path invokes it or reads its boolean.
 */
export const handleExclusiveSubscriptionRequestedTap = (context: NotificationTapContext): void => {
  const usersInfo = context.notification.info.usersInfo;

  if (context.notification.info.actioned === true) {
    Navigator.navigateToUserDetails(usersInfo.userId);
    return;
  }

  const requestNotificationId = context.notification.id;
  // Flutter null-asserts `notification.id!` (`:107`); in practice the id is
  // always populated (`firestoreService.getNotifications` spreads the doc id in
  // as `id`, `:2508`), so this is unreachable. Defensive no-op instead of a
  // crash — deliberately NOT falling back to the user-details navigate, which is
  // the *actioned* branch's behaviour and would silently mis-handle a request
  // that has not been reviewed.
  if (!requestNotificationId) return;

  ReviewExclusiveSubscriptionSheet.show({
    requestNotificationId,
    userId: usersInfo.userId,
    screenKey: context.screenKey,
  });
};

/**
 * `reportUser` (`push_navigation_service.dart:184-193`).
 *
 * Refreshes the current user when the notification is still unread — a report
 * can change the current user's own moderation state — then opens the reported
 * peer's profile.
 */
export const handleReportUserNotificationTap = (context: NotificationTapContext): void => {
  const info = context.notification.info;

  // Fire-and-forget, matching Flutter's un-awaited `UsersRepository().refreshUser()`
  // (`:187`): navigation must not wait on the re-fetch. `read !== true` treats an
  // absent `read` as unread, matching Flutter's non-nullable `bool read = false`
  // default (`notification_info.dart:31`).
  if (info.read !== true) {
    void context.refreshCurrentUser();
  }

  // NOTE: `peerUserId`, NOT `userId` — `reportUser` is the ONE case in this file
  // that navigates to the peer (`push_navigation_service.dart:190`); every other
  // user-details case above uses `usersInfo.userId`. Do not "fix" this to `userId`.
  const peerUserId = info.usersInfo.peerUserId;
  // `peerUserId` is optional on `UsersInfoEntity` while Flutter's param is
  // non-nullable; defensive no-op on a malformed document, not a behaviour change.
  if (!peerUserId) return;

  Navigator.navigateToUserDetails(peerUserId);
};

/** `exclusiveSubscription` → subscribers list (`push_navigation_service.dart:80`). */
export const handleSubscribersNotificationTap = (): void => {
  Navigator.navigateToSubscribers();
};

/**
 * `premiumPackage` / `identityVerification` / `premiumPackageExpired` → premium
 * packages (`push_navigation_service.dart:207`, one shared Flutter case body).
 */
export const handlePremiumPackagesNotificationTap = (): void => {
  Navigator.navigateToPremiumPackages();
};

/** `campaignRanking` → campaigns (`push_navigation_service.dart:231`). */
export const handleCampaignsNotificationTap = (): void => {
  Navigator.navigateToCampaigns();
};
